import { useFormContext } from "react-hook-form";
import { useState, useCallback, useRef } from "react";
import { BookIcon } from "lucide-react";

import { getModels } from "../utils";

import { useCustomParametersStore } from "./custom-parameters-store";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { AutoComplete } from "@/components/AutoCompleteInput";
import { Model, useModels } from "@/queries/models";
import { useProviders } from "@/queries/providers";
import { CustomProvider } from "@/queries/providers/types";
import { useAuth } from "@/context/auth-context";

export const InheritParametersDialog = () => {
  const auth = useAuth();
  const store = useCustomParametersStore();
  const form = useFormContext();
  const [selectedModel, setSelectedModel] = useState<Model | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const { isViewing, modelsList } = store;
  const [search, setSearch] = useState("");
  const { data: providers } = useProviders({
    userToken: auth?.userToken ?? "",
  });
  const isCustomProvider = !!providers?.find(
    (provider: CustomProvider) => provider.id === store.providerId && !!provider.workspace_id,
  );
  const providerId = isCustomProvider ? undefined : store.providerId;
  const {
    data: models,
    isLoading: modelsLoading,
    isFetching: modelsFetching,
  } = useModels({
    allowEmptySearch: true,
    providerId,
    q: search,
  });

  const modelName = form.getValues("create_custom_model.modelName");
  const provider = form.getValues("create_custom_model.provider");
  const modelType = form.getValues("create_custom_model.modelType");
  const isChat = modelType === "chat";
  const defaultModel =
    modelsList.find(model => model.llm_model_name === modelName) || ({} as Model);
  const isInheritable = store.inheritableParameters(defaultModel)?.length > 0;

  const isKnownModel = store.getKnownModel(modelName, provider, isChat);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  const onClick = () => {
    if (isKnownModel || selectedModel) {
      store.inheritParameters(selectedModel || defaultModel);
      setIsOpen(false);
      setSelectedModel(null);

      return;
    }

    setIsOpen(true);
  };

  const handleInputChange = useCallback(
    (value: string) => {
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }

      debounceRef.current = setTimeout(() => {
        setSearch(value);
      }, 300);
    },
    [setSearch],
  );

  return (
    <>
      <Button
        onClick={onClick}
        disabled={Boolean(!isInheritable && isKnownModel) || isViewing}
        variant="ghost"
        type="button"
      >
        <BookIcon className="mr-2 h-4 w-4" />
        Inherit parameters
      </Button>
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="flex flex-col gap-4">
          <DialogTitle>Inherit parameters</DialogTitle>
          <DialogDescription>
            We were unable to find any parameters for this model. Please select a model to inherit
            parameters from.
          </DialogDescription>
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-2">
              <Label>Model</Label>
              <AutoComplete
                addNewOption={false}
                isLoading={modelsLoading || modelsFetching}
                options={getModels(models?.model_configs ?? [])}
                value={selectedModel?.llm_model_name ?? null}
                onSelect={value =>
                  setSelectedModel(
                    models?.model_configs?.find(model => model.llm_model_name === value) || null,
                  )
                }
                onInputChange={handleInputChange}
                placeholder="Select a model"
                size="small"
              />
            </div>
          </div>
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setIsOpen(false)}>
              Cancel
            </Button>
            <Button type="button" disabled={modelsLoading} onClick={onClick}>
              Inherit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
